import { type StaticImageData } from "next/image";

import navBuyMatic from "@assets/images/header/nav-buy-matic.webp";
import navShopAccessories from "@assets/images/header/nav-shop-accessories.webp";
import navOrderStatus from "@assets/images/order-status/nav-box.png";
import { type PurchaseAssuranceIconKind } from "@/blueprint/components/PurchaseAssuranceIcon/PurchaseAssuranceIcon";
import { SITE_LINKS, type SiteLink } from "@/data/siteLinks";

export type Announcement = {
  /** Shown from tablet up, before the detail; null for none. */
  lead: string | null;
  detail: string;
  href: string;
  /** The check mark before the detail. */
  checkmark: boolean;
};

// The whole line is the detail, so it reads the same at every width.
export const ANNOUNCEMENT: Announcement = {
  lead: null,
  detail: "Quiet, now quieter. Meet Matic Serene",
  href: "/serene",
  checkmark: false,
};

export const PAYMENT_MARKS = {
  label: "Accepted payment methods",
  hsaFsa: "HSA/FSA",
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  paypal: "PayPal",
  googlePay: "Google Pay",
  amazonPay: "Amazon Pay",
  affirm: "Affirm",
};

/** Hover panels the desktop nav can open (Figma Desktop Nav states). */
export type NavHoverPanelId = "resources" | "shop";

type NavLink = {
  href: string | null;
  label: string;
  panel: NavHoverPanelId | null;
};

/** The nav entries that are real pages, in nav order. */
const NAV_PAGE_LINKS = [
  { href: "/serene", label: "Matic Serene", panel: null },
  { href: "/hey-matic", label: "Matic Cues", panel: null },
  { href: "/life-with-matic", label: "Life with Matic", panel: null },
  { href: "/innovation", label: "Innovation", panel: null },
] as const satisfies readonly NavLink[];

export const NAV_LINKS = [
  ...NAV_PAGE_LINKS,
  { href: null, label: "Resources", panel: "resources" },
] as const satisfies readonly NavLink[];

export const SHOP_HREF = SITE_LINKS.matic.href;
export const SHOP_LABEL = "Shop";

export type NavShopProduct = {
  title: string;
  meta: string;
  href: string;
  image: StaticImageData;
};

type NavShopAssurance = {
  icon: PurchaseAssuranceIconKind;
  title: string;
};

export const NAV_SHOP_PANEL = {
  buy: {
    title: "Buy Matic",
    meta: "From $1,495",
    href: SHOP_HREF,
    image: navBuyMatic,
  } satisfies NavShopProduct,
  accessories: {
    title: "Shop Accessories",
    meta: "Extra bags, rollers & more",
    href: SITE_LINKS.accessories.href,
    image: navShopAccessories,
  } satisfies NavShopProduct,
  orderStatus: {
    title: "Order Status",
    meta: "Track your shipment",
    href: SITE_LINKS.orderStatus.href,
    image: navOrderStatus,
  } satisfies NavShopProduct,
  assurances: [
    {
      icon: "return",
      title: "Try risk-free for 6 months",
    },
    {
      icon: "package",
      title: "Free shipping & returns",
    },
    {
      icon: "warranty",
      title: "1-year warranty",
    },
    {
      icon: "shipping",
      // Figma authors this break; without it the label orphans "weeks".
      title: "Ships in\n2 weeks",
    },
  ] satisfies readonly NavShopAssurance[],
};

// The sheet menu lists the pages; Resources is a desktop-only hover panel whose
// links the sheet renders separately as MENU_RESOURCE_LINKS. Derived from the
// nav order rather than indexed into it, so adding a page cannot silently drop
// one from the sheet.
export const MENU_PRIMARY_LINKS = NAV_PAGE_LINKS;

export const MENU_RESOURCE_LINKS: readonly SiteLink[] = [
  SITE_LINKS.support,
  SITE_LINKS.orderStatus,
  SITE_LINKS.warranty,
  SITE_LINKS.faq,
  SITE_LINKS.returnPolicy,
  SITE_LINKS.blog,
  SITE_LINKS.contact,
  SITE_LINKS.company,
];

type FooterColumn = {
  title: string;
  links: readonly SiteLink[];
};

export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    title: "Shop",
    links: [SITE_LINKS.matic, SITE_LINKS.accessories, SITE_LINKS.orderStatus],
  },
  {
    title: "Resources",
    links: [
      SITE_LINKS.support,
      SITE_LINKS.faq,
      SITE_LINKS.blog,
      SITE_LINKS.warranty,
      SITE_LINKS.returnPolicy,
      SITE_LINKS.hsaFsa,
    ],
  },
  {
    title: "Company",
    links: [
      SITE_LINKS.company,
      SITE_LINKS.careers,
      SITE_LINKS.terms,
      SITE_LINKS.press,
      SITE_LINKS.contact,
      SITE_LINKS.accessibility,
    ],
  },
  // SiteFooter appends the Cookie Settings trigger here (an action, not an href).
  {
    title: "Privacy",
    links: [SITE_LINKS.privacy, SITE_LINKS.privacyChoices],
  },
  {
    title: "Socials",
    links: [SITE_LINKS.twitter, SITE_LINKS.instagram, SITE_LINKS.linkedin],
  },
];

export const NEWSLETTER = {
  label: "Subscribe to Matic news",
  disclaimer: "(Sadly, we can’t clean your junk folder.)",
};

const SQ_FT_SEED = 500_000_000;
const SQ_FT_SEED_TIME_MS = Date.parse("2026-09-29T00:00:00.000Z");
const SQ_FT_PER_SECOND = 2_150_000 / 86_400;
// Matches the design's sq-ft-to-weekends ratio (200,470,111 : 28,638).
const SQ_FT_PER_WEEKEND = 7_000;

export type FooterStatId = "sq-ft-cleaned" | "weekends-saved";

type FooterStat = {
  id: FooterStatId;
  label: string;
  srLabel: string;
  seedValue: number;
  perSecond: number;
  seedTimeMs: number;
};

export const FOOTER_STATS: readonly FooterStat[] = [
  {
    id: "sq-ft-cleaned",
    label: "sq ft cleaned",
    srLabel: "over 500 million",
    seedValue: SQ_FT_SEED,
    perSecond: SQ_FT_PER_SECOND,
    seedTimeMs: SQ_FT_SEED_TIME_MS,
  },
  {
    id: "weekends-saved",
    label: "weekends saved",
    srLabel: "over 71 thousand",
    seedValue: Math.round(SQ_FT_SEED / SQ_FT_PER_WEEKEND),
    perSecond: SQ_FT_PER_SECOND / SQ_FT_PER_WEEKEND,
    seedTimeMs: SQ_FT_SEED_TIME_MS,
  },
];
